import React, { useState } from "react";
import { Link } from "@/lib/router";
import axios from "axios";
import { ArrowRight, Mail, Phone, MessageCircle } from "lucide-react";
import { Storefront } from "@phosphor-icons/react";
import { useLang } from "@/context/LanguageContext";
import { Logo } from "@/components/Logo";
import { SocialIcon, SOCIAL_LABELS } from "@/components/SocialIcons";
import { SITE } from "@/config/site";
import { TEXT, CONTACTS } from "@/i18n/content";
import { SHOP } from "@/i18n/shop";
import { useCatalogLinks } from "@/components/catalog/CatalogPage";
import { PRIVACY, COOKIES, TERMS, SALES, RETURNS, SHIPPING } from "@/i18n/legal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const { lang, t } = useLang();
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await axios.post(`${API}/subscribe`, { email, language: lang, source: "newsletter_footer" });
    } catch (_) {
      /* already subscribed or error — confirm anyway */
    }
    setDone(true);
    setEmail("");
  };

  const catalogLinks = useCatalogLinks();
  const links = [
    { to: "/", label: t(TEXT.nav.home) },
    { to: "/story", label: t(TEXT.nav.about) },
    { to: "/products", label: t(TEXT.nav.products) },
    { to: "/seasonal", label: t(TEXT.nav.seasonal) },
    { to: "/signature", label: t(TEXT.nav.signature) },
    { to: "/founder-club", label: t(TEXT.nav.founderClub) },
    { to: "/contact", label: t(TEXT.nav.contact) },
  ];

  return (
    <footer data-testid="footer" className="relative border-t border-white/10 bg-ink">
      {/* Newsletter band */}
      <div className="border-b border-white/10">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-6 py-16 lg:grid-cols-2 lg:px-8">
          <div>
            <h2 className="font-display text-3xl font-light tracking-tight text-gold sm:text-4xl">{t(TEXT.footer.newsletterTitle)}</h2>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-white/55">{t(TEXT.footer.newsletterDesc)}</p>
          </div>
          {done ? (
            <p data-testid="newsletter-success" className="text-lg font-light text-gold lg:text-right">{t(TEXT.footer.subscribed)}</p>
          ) : (
            <div className="lg:ml-auto lg:w-full lg:max-w-md">
              <form onSubmit={subscribe} className="flex items-center border-b border-white/20 focus-within:border-gold">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t(TEXT.footer.emailPlaceholder)}
                  data-testid="newsletter-email"
                  className="w-full bg-transparent py-3 text-sm text-white placeholder-white/40 outline-none"
                />
                <button type="submit" data-testid="newsletter-submit" className="flex items-center gap-2 whitespace-nowrap py-3 pl-4 text-xs uppercase tracking-widest text-gold transition-transform hover:translate-x-1">
                  {t(TEXT.footer.subscribe)}
                  <ArrowRight size={16} strokeWidth={1.5} />
                </button>
              </form>
              <p data-testid="newsletter-consent" className="mt-3 text-xs leading-relaxed text-white/40">
                {t(TEXT.footer.newsletterConsent)}{" "}
                <Link to="/privacy" data-testid="newsletter-privacy-link" className="text-gold/80 underline-offset-2 hover:underline">{t(PRIVACY.title)}</Link>.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl gap-12 px-6 py-16 lg:grid-cols-[1.5fr_1fr_1fr] lg:px-8">
        <div>
          <Logo variant="mark" className="h-12 w-auto" />
          <p className="mt-5 max-w-xs text-sm leading-relaxed text-white/50">{t(TEXT.footer.tagline)}</p>
          <div className="mt-5 inline-flex items-center gap-2 border border-gold/30 bg-gold/5 px-3.5 py-1.5" data-testid="footer-made-in-italy">
            <span className="h-1.5 w-1.5 rounded-full bg-gold" />
            <span className="text-[10px] uppercase tracking-overline text-gold">{t(TEXT.footer.madeIn)}</span>
          </div>
          <div className="mt-7" data-testid="footer-social">
            <span className="text-[10px] uppercase tracking-overline text-white/35">Follow VORNX</span>
            <div className="mt-3 flex items-center gap-3">
              {Object.keys(SITE.social).filter((key) => SITE.social[key] && key !== "whatsapp").map((key) => {
                const href = SITE.social[key];
                const common = "flex h-9 w-9 items-center justify-center border border-white/15 text-white/55 transition-colors";
                return (
                  <a
                    key={key}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={SOCIAL_LABELS[key]}
                    data-testid={`social-${key}`}
                    className={`${common} hover:border-gold hover:text-gold`}
                  >
                    <SocialIcon name={key} />
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-xs uppercase tracking-overline text-gold">{t(TEXT.footer.nav)}</h3>
          <ul className="mt-6 space-y-3">
            <li>
              <Link to="/shop" data-testid="footer-boutique" className="inline-flex items-center gap-2 text-sm font-medium text-gold transition-colors hover:text-gold-soft">
                <Storefront size={15} weight="light" /> {t(SHOP.nav)}
              </Link>
            </li>
            {links.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-sm text-white/60 transition-colors hover:text-white">{l.label}</Link>
              </li>
            ))}
            {catalogLinks.map((c) => (
              <li key={c.id}>
                <Link to={c.path} data-testid={`footer-catalog-${c.id}`} className="text-sm text-white/60 transition-colors hover:text-white">{c.h1}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="text-xs uppercase tracking-overline text-gold">{t(TEXT.footer.contact)}</h3>
          <ul className="mt-6 space-y-4 text-sm text-white/60">
            <li className="flex items-center gap-3">
              <Mail size={16} strokeWidth={1.5} className="text-gold" />
              <a href={`mailto:${CONTACTS.email}`} data-testid="footer-email" className="transition-colors hover:text-gold">{CONTACTS.email}</a>
            </li>
            <li className="flex items-center gap-3">
              <Phone size={16} strokeWidth={1.5} className="text-gold" />
              <a href="tel:+390761770180" data-testid="footer-phone" className="transition-colors hover:text-gold">+39 0761 770180</a>
            </li>
            {SITE.social.whatsapp && (
              <li className="flex items-center gap-3">
                <MessageCircle size={16} strokeWidth={1.5} className="text-gold" />
                <a href={SITE.social.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="transition-colors hover:text-gold">WhatsApp</a>
              </li>
            )}
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto max-w-3xl px-6 pt-8 lg:px-8">
          <p data-testid="footer-disclaimer" className="text-center text-[11px] leading-relaxed tracking-wide text-white/30">
            {t(TEXT.footer.disclaimer)}
          </p>
        </div>
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-6 py-6 text-xs text-white/40 sm:flex-row lg:px-8">
          <span>© {new Date().getFullYear()} VORNX. {t(TEXT.footer.rights)}</span>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 sm:justify-start" data-testid="footer-legal">
            <Link to="/privacy" className="transition-colors hover:text-gold">{t(PRIVACY.title)}</Link>
            <Link to="/terms" className="transition-colors hover:text-gold">{t(TERMS.title)}</Link>
            <Link to="/sales" className="transition-colors hover:text-gold">{t(SALES.title)}</Link>
            <Link to="/returns" className="transition-colors hover:text-gold">{t(RETURNS.title)}</Link>
            <Link to="/shipping" className="transition-colors hover:text-gold">{t(SHIPPING.title)}</Link>
            <Link to="/cookies" className="transition-colors hover:text-gold">{t(COOKIES.title)}</Link>
          </div>
          <span className="tracking-widest">Made in Italy</span>
        </div>
      </div>
    </footer>
  );
};
